/**
 * The board canvas and all pointer interaction.
 *
 * Pointer Events are used throughout, so mouse, pen and touch take exactly one
 * code path — that is what makes touch annotation work without a second
 * interaction model. Desktop remains the design centre: hover feedback and
 * keyboard shortcuts are first-class, touch degrades sensibly (bigger hit slop,
 * `touch-action: none` so a drag never scrolls the page).
 *
 * A render loop runs continuously rather than on state change. It costs one
 * cheap 2D repaint per frame and guarantees `canvas.captureStream()` always has
 * fresh frames while recording.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import {
  carriedEntities,
  carryPoses,
  findEntityType,
  findRole,
  findTeam,
  glidePoses,
  PACE_MS_PER_FIELD,
  clampToView,
  confineShape,
  heldEntity,
  holderOf,
  lastHolder,
  idsInRect,
  inView,
  simplifyPath,
  type BoardView,
  type EntityMove,
  type PoseMap,
  type ShapeKind,
  type Vec2,
} from '../core/index.ts';
import {
  hitTestEntity,
  hitTestPositionLabel,
  hitTestShape,
  POSITION_FONT,
  POSITION_OFFSET,
  positionLabel,
  px,
  render,
  toField,
  toScreen,
  type Scene,
  type Viewport,
} from '../renderer/index.ts';
import { BALL_TOOLS, CARRY_TOOLS, DRAW_TOOLS, RELEASE_TOOLS, type BoardApi } from './useBoard.ts';
import { pointerFor } from './pointers.ts';
import type { BoardFrame } from './recordingFrame.ts';

type Interaction =
  | {
      readonly mode: 'drag';
      pointerId: number;
      /** Every piece that moves, with where each one started. */
      readonly starts: Readonly<Record<string, Vec2>>;
      /** The piece under the pointer at press time; a click narrows the selection to it. */
      readonly pressedId: string;
      readonly pointerStart: Vec2;
      /** The press point in screen pixels, which is what slop is measured in. */
      readonly screenStart: Vec2;
      /** How far, in screen pixels, the pointer must travel before this counts as a move. */
      readonly slopPx: number;
      pointer: Vec2;
      /** Whether the pointer has left the press point by more than slop; a plain click is not a move. */
      moved: boolean;
      dirty: boolean;
    }
  | {
      /** A rubber band from open water; whatever ends up inside is selected. */
      readonly mode: 'select';
      pointerId: number;
      readonly origin: Vec2;
      corner: Vec2;
    }
  | {
      readonly mode: 'draw';
      pointerId: number;
      kind: ShapeKind;
      points: Vec2[];
      /**
       * The entity this stroke moves: the player a swim is drawn from, or
       * the ball a pass or a shot sends away. Absent for a plain annotation.
       */
      readonly moverId?: string;
      /** For a pass or a shot: the player holding the ball as it was drawn. */
      readonly fromId?: string;
      /** What travels with it, the mover excluded. */
      readonly carriedIds: readonly string[];
    };

interface BoardProps {
  readonly api: BoardApi;
  readonly canvasRef: RefObject<HTMLCanvasElement | null>;
  /**
   * Set while a recording runs: called on every animation frame with what the
   * board shows and where the coach's pointer is, so the video can be made
   * from it after they stop. On screen, nothing changes while recording.
   */
  readonly onFrame?: ((frame: BoardFrame) => void) | undefined;
}

/** Where the pointer is over the canvas, in CSS pixels, and whether it is pressed. */
interface PointerState {
  x: number;
  y: number;
  kind: string;
  inside: boolean;
  down: boolean;
}

const TOUCH_SLOP_PX = 14;
const MOUSE_SLOP_PX = 4;

/** Every piece's start shifted by how far the pointer has travelled since the press. */
/** Each piece clamps on its own at the edge of what the view shows. */
function groupMoves(drag: Extract<Interaction, { mode: 'drag' }>, view: BoardView): EntityMove[] {
  const dx = drag.pointer.x - drag.pointerStart.x;
  const dy = drag.pointer.y - drag.pointerStart.y;
  return Object.entries(drag.starts).map(([id, at]) => ({
    id,
    to: clampToView({ x: at.x + dx, y: at.y + dy }, view),
  }));
}

/**
 * Only the pieces the coach can see can be picked up: a piece in the half the
 * view crops away still exists, but a press in the padding past the window's
 * edge must not grab it.
 */
function visiblePoses(poses: PoseMap, view: BoardView): PoseMap {
  if (view === 'full') return poses;
  return Object.fromEntries(Object.entries(poses).filter(([, at]) => inView(at, view)));
}

/** Two presses on the same target within this long open its editor. */
const DOUBLE_PRESS_MS = 400;

/** What a double-press edits, and where the field sits over the canvas. */
interface Editing {
  readonly entityId: string;
  readonly field: 'number' | 'position';
  readonly value: string;
  /** Centre of the field, in canvas CSS pixels. */
  readonly at: Vec2;
  readonly refused?: string;
}

export function Board({ api, canvasRef, onFrame }: BoardProps): JSX.Element {
  const pointerRef = useRef<PointerState>({ x: 0, y: 0, kind: 'mouse', inside: false, down: false });
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const [editing, setEditing] = useState<Editing | null>(null);
  const lastPressRef = useRef<{ time: number; entityId: string; field: Editing['field'] } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<Viewport | null>(null);
  const interactionRef = useRef<Interaction | null>(null);
  const hoverRef = useRef<string | null>(null);
  const apiRef = useRef(api);
  apiRef.current = api;

  /* ---------------- canvas sizing (device pixel ratio aware) ---------------- */

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const resize = (): void => {
      const rect = container.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    window.addEventListener('orientationchange', resize);
    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', resize);
    };
  }, [canvasRef]);

  /* ---------------- continuous render loop ---------------- */

  useEffect(() => {
    let frame = 0;
    const draw = (): void => {
      frame = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const current = apiRef.current;
      const interaction = interactionRef.current;

      // Apply an in-flight drag at most once per frame.
      if (interaction?.mode === 'drag' && interaction.dirty) {
        interaction.dirty = false;
        current.dragEntities(groupMoves(interaction, current.view), false);
      }

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // The stroke is previewed exactly as it will be kept: an arrow stops at
      // the field's edge while the pen carries on into the surround.
      const drawn = interaction?.mode === 'draw' ? confineShape(interaction.kind, interaction.points) : [];
      // While a lane is in flight its piece follows the pen, and the ball with
      // it if they have it: the coach drafting a phase sees the move they are
      // making. This is a render-time override, so the document — and the undo
      // stack — stay untouched until the pointer lifts.
      // Just after a formation is recalled, the pieces glide to it from where
      // they stood. Drawn here, frame by frame, so the recording has it too.
      const onScreen = current.glide
        ? glidePoses(current.glide.from, current.poses, performance.now() - current.glide.startedAt, {
            aspectRatio: current.pack.surface.aspectRatio,
            paceMs: PACE_MS_PER_FIELD,
          }).poses
        : current.poses;
      const drawnTo =
        interaction?.mode === 'draw' && interaction.moverId ? drawn[drawn.length - 1] : undefined;

      const scene: Scene = {
          pack: current.pack,
          doc: current.doc,
          poses:
            drawnTo && interaction?.mode === 'draw' && interaction.moverId
              ? carryPoses(onScreen, interaction.moverId, interaction.carriedIds, drawnTo)
              : onScreen,
          shapes: current.shapes,
          selectedIds: current.selectedIds,
          hoverId: hoverRef.current,
          draft:
            interaction?.mode === 'draw' && drawn.length > 1
              ? {
                  kind: interaction.kind,
                  points: drawn,
                  color: current.color,
                  width: current.strokeWidth,
                }
              : null,
          showRoleLabels: current.showRoleLabels,
          showVenueDetail: current.showVenueDetail,
          view: current.view,
          marquee:
            interaction?.mode === 'select' ? { from: interaction.origin, to: interaction.corner } : null,
      };
      const vp = render(ctx, scene, width, height);
      viewportRef.current = vp;

      // While recording, log what was just drawn, and the pointer: a mouse
      // wherever it is over the board, a finger or pen while it touches. The
      // video is made from this log after the coach stops.
      const log = onFrameRef.current;
      if (log) {
        const pointer = pointerRef.current;
        const shown = pointer.inside && (pointer.kind === 'mouse' || pointer.down);
        log(shown ? { scene, pointer: { at: toField(pointer.x, pointer.y, vp), tool: current.tool, unit: vp.unit } } : { scene });
      }
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [canvasRef]);

  /* ---------------- pointer handling ---------------- */

  const localPoint = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>): Vec2 => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    },
    [canvasRef],
  );

  /* ---------------- editing a number or a position in place ---------------- */

  const openEditor = useCallback(
    (entityId: string, field: Editing['field'], vp: Viewport) => {
      const entity = api.doc.entities.find((e) => e.id === entityId);
      const at = api.poses[entityId];
      const type = entity ? findEntityType(api.pack, entity.typeId) : undefined;
      if (!entity || !at || !type) return;
      const centre = toScreen(at, vp);
      const radius = px(type.radius, vp);
      setEditing({
        entityId,
        field,
        value: field === 'number' ? entity.label : (positionLabel(entity, findRole(api.pack, entity.roleId)) ?? ''),
        at:
          field === 'number'
            ? centre
            : { x: centre.x, y: centre.y + radius * (POSITION_OFFSET + POSITION_FONT * 0.55) },
      });
    },
    [api],
  );

  /**
   * `save` keeps an edit open when it is refused, so the coach can fix it;
   * `blur` (clicking away) drops a refused edit instead of leaving a field
   * stranded without focus; `cancel` discards.
   */
  const closeEditor = useCallback(
    (how: 'save' | 'blur' | 'cancel') => {
      if (!editing) return;
      if (how === 'cancel') return setEditing(null);
      if (editing.field === 'position') {
        api.renamePosition(editing.entityId, editing.value);
        return setEditing(null);
      }
      if (api.renamePlayer(editing.entityId, editing.value) || how === 'blur') return setEditing(null);
      // Refused: an empty number, or one the team already has. Stay open.
      const team = findTeam(api.pack, api.doc.entities.find((e) => e.id === editing.entityId)?.teamId);
      const refused = editing.value.trim()
        ? `${team?.name ?? 'This team'} already has ${editing.value.trim()}.`
        : 'A player needs a number.';
      setEditing({ ...editing, refused });
    },
    [api, editing],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>): void => {
      if (interactionRef.current) return; // ignore additional fingers
      const vp = viewportRef.current;
      if (!vp) return;

      const screen = localPoint(event);
      const field = toField(screen.x, screen.y, vp);
      const slop = event.pointerType === 'mouse' ? MOUSE_SLOP_PX : TOUCH_SLOP_PX;
      event.currentTarget.setPointerCapture(event.pointerId);

      if (api.tool === 'erase') {
        const shape = hitTestShape(api.shapes, screen, vp, slop);
        if (shape) api.eraseShape(shape.id);
        return;
      }

      if (api.tool === 'select') {
        const visible = visiblePoses(api.poses, api.view);
        const entity = hitTestEntity(api.pack, api.doc.entities, visible, screen, vp, slop);
        // A second press on the same cap or position label opens its editor;
        // the first one has already selected the player.
        const label = !entity && api.showRoleLabels
          ? hitTestPositionLabel(api.pack, api.doc.entities, visible, screen, vp, slop / 2)
          : null;
        const target = entity
          ? { entityId: entity.id, field: 'number' as const }
          : label
            ? { entityId: label.id, field: 'position' as const }
            : null;
        const previous = lastPressRef.current;
        const now = performance.now();
        if (
          target &&
          previous &&
          previous.entityId === target.entityId &&
          previous.field === target.field &&
          now - previous.time < DOUBLE_PRESS_MS
        ) {
          lastPressRef.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
          // Without this the browser finishes the press by moving focus to
          // where it landed — away from the field that has just opened, which
          // would read as clicking away and close it again at once.
          event.preventDefault();
          openEditor(target.entityId, target.field, vp);
          return;
        }
        lastPressRef.current = target ? { ...target, time: now } : null;
        if (label && !entity) {
          // A press on a label is aimed at the label, not at open water: it
          // neither clears the selection nor starts a rubber band.
          return;
        }
        if (entity) {
          // A press on a piece inside the selection drags the whole selection;
          // on any other piece it becomes the selection on its own.
          const ids = api.selectedIds.includes(entity.id) ? api.selectedIds : [entity.id];
          if (ids !== api.selectedIds) api.setSelectedIds(ids);
          const starts: Record<string, Vec2> = {};
          for (const id of ids) {
            const at = api.poses[id];
            if (at) starts[id] = at;
          }
          interactionRef.current = {
            mode: 'drag',
            pointerId: event.pointerId,
            starts,
            pressedId: entity.id,
            pointerStart: field,
            screenStart: screen,
            slopPx: slop,
            pointer: field,
            moved: false,
            dirty: false,
          };
        } else {
          // Open water: the selection is cleared, and a drag from here is a rubber band.
          api.setSelectedIds([]);
          interactionRef.current = {
            mode: 'select',
            pointerId: event.pointerId,
            origin: field,
            corner: field,
          };
        }
        return;
      }

      if (DRAW_TOOLS.includes(api.tool)) {
        const kind = api.tool as ShapeKind;
        const carries = CARRY_TOOLS.includes(kind);
        const releases = RELEASE_TOOLS.includes(kind);
        // What the stroke starts on decides what it moves. From open water it
        // moves nothing and is only an annotation.
        const start =
          carries || releases
            ? hitTestEntity(api.pack, api.doc.entities, visiblePoses(api.poses, api.view), screen, vp, slop)
            : undefined;

        // Who has the ball is one rule: the nearest player within reach, and on
        // a tie whoever had it in the phase before. So a defender swimming
        // beside the attacker never takes it, nor can pass it.
        const before = lastHolder(api.doc, api.pack, api.activeKeyframeId);
        // A swim takes the player it is drawn from, and the ball if they have
        // it; a pass or a shot sends away the ball they have and leaves the
        // player where they are. A swim begun on a held ball means its holder:
        // the ball sits at the player's edge, and it is the player being moved.
        const moverId = start
          ? releases
            ? heldEntity(api.doc, api.pack, api.poses, start.id, before)
            : (holderOf(api.doc, api.pack, api.poses, start.id, before) ?? start.id)
          : undefined;
        const carriedIds =
          moverId && carries
            ? carriedEntities(api.doc, api.pack, api.poses, moverId, BALL_TOOLS.includes(kind), before).slice(1)
            : [];
        // Who plays a pass: the player it was drawn from, or, drawn from the
        // ball, whoever holds it now. Playback waits for them, so a player who
        // swims to the ball reaches it before it leaves.
        const fromId =
          releases && start && moverId
            ? start.id !== moverId
              ? start.id
              : holderOf(api.doc, api.pack, api.poses, moverId, before)
            : undefined;

        interactionRef.current = {
          mode: 'draw',
          pointerId: event.pointerId,
          kind,
          points: [field],
          carriedIds,
          ...(moverId ? { moverId } : {}),
          ...(fromId ? { fromId } : {}),
        };
      }
    },
    [api, localPoint, openEditor],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>): void => {
      const vp = viewportRef.current;
      if (!vp) return;
      const screen = localPoint(event);
      const field = toField(screen.x, screen.y, vp);
      const interaction = interactionRef.current;

      if (!interaction) {
        if (event.pointerType === 'mouse' && api.tool === 'select') {
          const entity = hitTestEntity(api.pack, api.doc.entities, visiblePoses(api.poses, api.view), screen, vp, MOUSE_SLOP_PX);
          hoverRef.current = entity?.id ?? null;
        }
        return;
      }
      if (interaction.pointerId !== event.pointerId) return;

      if (interaction.mode === 'drag') {
        // Sub-pixel jitter (mouse noise, a touch digitiser's tiny wobble) must
        // not read as a move: that would preview a drift, record an undo step
        // — and, with a touch tap, autosave — for what the coach experienced
        // as a click. So nothing is previewed, either, until slop is cleared;
        // a sub-slop press-and-release then leaves the document untouched.
        if (!interaction.moved) {
          const dx = screen.x - interaction.screenStart.x;
          const dy = screen.y - interaction.screenStart.y;
          interaction.moved = Math.hypot(dx, dy) > interaction.slopPx;
        }
        if (interaction.moved) {
          interaction.pointer = field;
          interaction.dirty = true;
        }
      } else if (interaction.mode === 'select') {
        interaction.corner = field;
      } else {
        const last = interaction.points[interaction.points.length - 1];
        // Drop sub-pixel samples; touch digitisers emit a lot of them.
        if (!last || Math.hypot(field.x - last.x, field.y - last.y) > 0.0015) {
          interaction.points.push(field);
        }
      }
    },
    [api, localPoint],
  );

  const finishInteraction = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>): void => {
      const interaction = interactionRef.current;
      if (!interaction || interaction.pointerId !== event.pointerId) return;
      interactionRef.current = null;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      // A press that became a drag or a box is not half of a double-press.
      if (interaction.mode !== 'drag' || interaction.moved) lastPressRef.current = null;

      if (interaction.mode === 'drag') {
        // A click that never moved records nothing; otherwise one commit for
        // the whole group, so one undo step brings it all back.
        if (interaction.moved && Object.keys(interaction.starts).length > 0) {
          api.dragEntities(groupMoves(interaction, api.view), true);
        } else if (!interaction.moved && api.selectedIds.length > 1) {
          // A click (no drag) on a group member narrows the selection to it.
          api.setSelectedIds([interaction.pressedId]);
        }
        return;
      }
      if (interaction.mode === 'select') {
        // A click without movement is an empty box, and so clears the selection.
        api.setSelectedIds(idsInRect({ from: interaction.origin, to: interaction.corner }, visiblePoses(api.poses, api.view)));
        return;
      }

      const simplified = simplifyPath(interaction.points, interaction.kind === 'freehand' ? 0.003 : 0.008);
      // An arrow drawn wholly outside the field leaves nothing to keep, and
      // then nothing moves either: the preview showed no arrow, so committing
      // a movement would be a surprise.
      const kept = confineShape(interaction.kind, simplified);
      if (kept.length < 2) return;
      api.commitShape(
        interaction.kind,
        kept,
        // The board resolved what moves when the stroke began and has been
        // drawing it ever since; handing that same intent over is what keeps
        // the commit identical to the preview, and makes the stroke a lane.
        interaction.moverId
          ? {
              moverId: interaction.moverId,
              carriedIds: interaction.carriedIds,
              to: kept[kept.length - 1]!,
              ...(interaction.fromId ? { fromId: interaction.fromId } : {}),
            }
          : undefined,
      );
    },
    [api],
  );

  // The app's own pointer, on screen and in recordings alike: the video draws
  // the very same image where the coach's pointer was.
  const cursor = pointerFor(api.tool).css;

  /** Keeps `pointerRef` up to date; the interaction handlers stay as they were. */
  const track = (event: ReactPointerEvent<HTMLCanvasElement>, change: Partial<PointerState>): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    Object.assign(pointerRef.current, {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      kind: event.pointerType,
      ...change,
    });
  };

  return (
    <div className="board" ref={containerRef} data-tutorial="board">
      <canvas
        ref={canvasRef}
        className="board__canvas"
        style={{ cursor, touchAction: 'none' }}
        onPointerDown={(event) => {
          track(event, { inside: true, down: true });
          onPointerDown(event);
        }}
        onPointerMove={(event) => {
          track(event, { inside: true });
          onPointerMove(event);
        }}
        onPointerUp={(event) => {
          track(event, { down: false });
          finishInteraction(event);
        }}
        onPointerCancel={(event) => {
          track(event, { down: false, inside: false });
          finishInteraction(event);
        }}
        onPointerLeave={(event) => {
          track(event, { inside: false, down: false });
          hoverRef.current = null;
        }}
        onContextMenu={(event) => event.preventDefault()}
      />
      {editing && (
        <>
          <input
            className={`board__editor ${editing.refused ? 'board__editor--refused' : ''}`}
            style={{ left: editing.at.x, top: editing.at.y }}
            aria-label={editing.field === 'number' ? 'Player number' : 'Position name'}
            value={editing.value}
            // A cap number is short; a position name may be a word or two.
            maxLength={editing.field === 'number' ? 3 : 24}
            autoFocus
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => {
              const value = event.target.value;
              setEditing((current) => (current ? { entityId: current.entityId, field: current.field, at: current.at, value } : null));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') closeEditor('save');
              else if (event.key === 'Escape') closeEditor('cancel');
            }}
            onBlur={() => closeEditor('blur')}
          />
          {editing.refused && (
            <span className="board__editor-hint" style={{ left: editing.at.x, top: editing.at.y }} role="alert">
              {editing.refused}
            </span>
          )}
        </>
      )}
    </div>
  );
}
